import {
    Autocomplete,
    Badge,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Popper,
    Select,
    Stack,
    styled,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getProposalData } from "../Services/Proposals";
import {
    DataGridPro,
    GridColDef,
    GridPinnedColumnFields
} from "@mui/x-data-grid-pro";
import { ThreeDot } from "react-loading-indicators";
import emptyImage from "../../assets/images/image.png";
import defaultlogo from "../../assets/icons/noimg.png";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Grid from "@mui/material/Grid2";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import DeleteIcon from "@mui/icons-material/Delete";
import {
    accountManager,
    accountManagerHead,
    generateCompanyLogo,
    generatProfilePicUrl,
    isAccountManager,
    isManagement,
    Management,
    proposalDownloadURL
} from "../../Utils/Constant";
import {
    getCompanyRfqMetaFilterData,
    getCURFQAssignedToMe,
    getProposalsFilterData
} from "../Services/OrgRepo";
import { getCURFQAssignedToMeFilterMetaFieldsApi } from "../Services/FeasibleRFQ";
import { showToast } from "../../Components/Toast";
import {
    handleDeleteProposal,
    handleUploadProposal,
    parseCustomDate,
    updateProposalStatus
} from "./ProposalHandlers";
import { useConfirmDelete } from "./useConfirmDelete";
import { useDispatch } from "react-redux";
import { resetCopoData } from "../../Redux/CoPo/actions";

export const Proposals = () => {
    const dispatch = useDispatch();
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [fileUploadLoading, setFileUploadLoading] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [allRows, setAllRows] = useState([]);

    const searchInputRef = useRef<HTMLInputElement>(null);

    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 10 // Default page size
    });
    const [pinnedColumns, setPinnedColumns] =
        React.useState<GridPinnedColumnFields>({
            left: ["CORFQ_No", "CORFQ_Name"],
            right: ["Upload_Proposal", "Action"]
        });
    const handlePinnedColumnsChange = React.useCallback(
        (updatedPinnedColumns: GridPinnedColumnFields) => {
            setPinnedColumns(updatedPinnedColumns);
        },
        []
    );
    const popperRef = useRef<HTMLDivElement>(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [isFilterLoading, setIsFilterLoading] = useState<boolean>(false);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [selectedKeyAccountManager, setSelectedKeyAccountManager] = useState(
        []
    );
    const [selectedCompany, setSelectedCompany] = useState([]);
    const [filteredRows, setFilteredRows] = useState(rows);
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const [filteredSelectedStatus, setFilteredSelectedStatus] = useState([]);
    const [
        filteredSelectedKeyAccountManager,
        setFilteredSelectedKeyAccountManager
    ] = useState([]);
    const [filteredSelectedCompany, setFilteredSelectedCompany] = useState([]);
    const [filterOptions, setFilterOptions] = useState<{
        [key: string]: { title: string; value: string }[];
    }>({});
    const [isApplyFilter, setIsApplyFilter] = useState<Boolean>(false);
    const [removeFilter, setRemoveFilter] = useState<boolean>(false);
    const { confirm, ConfirmDialog } = useConfirmDelete();
    // example: in parent component
    const [isUploadingGlobal, setIsUploadingGlobal] = useState(false);

    const VisuallyHiddenInput = styled("input")({
        clip: "rect(0 0 0 0)",
        clipPath: "inset(50%)",
        height: 1,
        overflow: "hidden",
        position: "absolute",
        bottom: 0,
        left: 0,
        whiteSpace: "nowrap",
        width: 1,
        background: "none"
    });

    const fetchProposalData = async () => {
        setIsLoading(true);
        try {
            const corfqData = await getCURFQAssignedToMe();
            const proposalsResponse = await getProposalData("");

            const proposals = proposalsResponse?.data || [];

            // Merge by CORFQ_No:
            const mergedRows = proposals.map((proposal) => {
                const matchingCURFQ = corfqData?.data?.find(
                    (item) => item.curfq_no === proposal.CORFQ_No
                );

                return {
                    id: `${proposal.CORFQ_No}`,
                    ...proposal,
                    curfq_id: matchingCURFQ?.CORFQ_ID || null
                };
            });

            setAllRows(mergedRows);
            setRows(mergedRows);
        } catch (err) {
            console.error("Failed to fetch proposals data", err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchProposalData();
    }, []);

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const search = event?.target?.value;
        setSearchTerm(search);

        if (search?.length >= 3) {
            const filtered = allRows?.filter((row) =>
                ["CORFQ_No", "CORFQ_Name"].some(
                    (key) =>
                        typeof row[key] === "string" &&
                        row[key].toLowerCase().includes(search?.toLowerCase())
                )
            );
            setRows(filtered);
        } else if (search === "") {
            setRows(allRows);
        }
    };

    const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (Object.keys(filterOptions)?.length === 0) {
            fetchFilterData();
        }
        setAnchorEl(event.currentTarget);
    };

    /**Filter functionality start from here */
    function extractOptions(apiResponse: any[], keyName: string) {
        const section = apiResponse.find((item) => item[keyName]);
        if (!section) return [];

        // Handle keyAccountManager separately as it is a direct array
        if (Array.isArray(section[keyName])) {
            return section[keyName].map((name) => ({
                key: name,
                title: name
            }));
        }

        // Handle objects with choices
        if (section[keyName]?.choices) {
            return section[keyName].choices.map(
                (item: { key: any; link_name: any; value: any }) => ({
                    key: item.key || item.link_name || "unknown_key",
                    title: item.value || item.link_name || "Unknown"
                })
            );
        }

        return [];
    }

    const fetchFilterData = async () => {
        setIsFilterLoading(true);
        try {
            const filterData = await getCompanyRfqMetaFilterData();
            const filterDataStatus = await getProposalsFilterData();
            const filterDataKam =
                await getCURFQAssignedToMeFilterMetaFieldsApi();

            let apiResponseCompany = [];
            let apiResponseStatus = [];

            if (filterData?.status) {
                apiResponseCompany = filterData?.data;
            }
            if (filterDataStatus?.status) {
                apiResponseStatus = filterDataStatus?.data;
            }
            type CompanyType = {
                company: string[];
            };
            const companyDetails: string[] = Array.from(
                new Set(rows?.map((item: any) => item?.Company_Name) ?? [])
            );
            const company: CompanyType[] = [{ company: companyDetails }];

            const apiResponse = filterDataStatus?.data;
            // console.log("apiResponse", apiResponse);

            const statusOptions = apiResponse?.Proposal_Status?.map(
                (status: string) => ({
                    key: status,
                    title: status
                })
            );

            // console.log("statusOptions", statusOptions);

            const keyAccountManagerOptions = extractOptions(
                filterDataKam?.data,
                "key_account_managers"
            );
            const companyOptions = extractOptions(company, "company");

            const obj = {
                statusOptions,
                keyAccountManagerOptions,
                companyOptions
            };
            setFilterOptions(obj);
        } catch (error) {
            console.error("Error fetching filter data:", error);
        } finally {
            setIsFilterLoading(false);
        }
    };

    const filterData = (
        rows: any[],
        filters: {
            status: any;
            keyAccountManager: any;
            company: any;
        }
    ) => {
        return rows?.filter(
            (row: {
                Proposal_Status: string;
                KAM: string;
                Company_Name: any;
            }) => {
                const statusArray =
                    row?.Proposal_Status?.toLowerCase()?.split(/\s*,\s*/) || [];
                const keyAccountManagerArray =
                    row?.KAM?.toLowerCase()?.split(/\s*,\s*/) || [];
                const companyArray =
                    row?.Company_Name?.toLowerCase().split(/\s*,\s*/) || [];
                // Get filter values, ignore empty ones
                const {
                    status = [],
                    keyAccountManager = [],
                    company = []
                } = filters;

                const statusMatch =
                    status?.length === 0 ||
                    status?.some((sol: string) =>
                        statusArray?.includes(sol.toLowerCase())
                    );

                const keyAccounManagerMatch =
                    keyAccountManager?.length === 0 ||
                    keyAccountManager?.some((loc: string) =>
                        keyAccountManagerArray.includes(loc.toLowerCase())
                    );
                const companyMatch =
                    company?.length === 0 ||
                    company?.some((cert: string) =>
                        companyArray?.includes(cert.toLowerCase())
                    );

                // **Must match ALL applied filters**
                return statusMatch && keyAccounManagerMatch && companyMatch;
            }
        );
    };

    const handleApply = async () => {
        setIsApplyFilter(true);
        let filters = {
            status:
                selectedStatus?.map((item) => item?.title.toLowerCase()) || [],
            keyAccountManager:
                selectedKeyAccountManager?.map((item) =>
                    item?.title.toLowerCase()
                ) || [],
            company:
                selectedCompany?.map((item) => item?.title.toLowerCase()) || []
        };

        const filteredResults = filterData(rows, filters);
        setFilteredRows(filteredResults);

        setAnchorEl(null);
        setFilteredSelectedStatus(selectedStatus);
        setFilteredSelectedKeyAccountManager(selectedKeyAccountManager);
        setFilteredSelectedCompany(selectedCompany);

        if (
            selectedStatus?.length == 0 &&
            selectedKeyAccountManager?.length == 0 &&
            selectedCompany?.length === 0
        ) {
            setFilteredRows(rows);
            setFilteredSelectedStatus(selectedStatus);
            setFilteredSelectedKeyAccountManager(selectedKeyAccountManager);
            setFilteredSelectedCompany(selectedCompany);
        }
    };

    const handleRemoveFilter = (category: string, item: { title: any }) => {
        if (removeFilter == true) {
            setRemoveFilter(false);
        } else {
            setRemoveFilter(true);
        }
        switch (category) {
            case "status":
                setSelectedStatus((prev) =>
                    prev.filter((i) => i.title !== item.title)
                );
                break;
            case "keyAccountManager":
                setSelectedKeyAccountManager((prev) =>
                    prev.filter((i) => i.title !== item.title)
                );
                break;
            case "company":
                setSelectedCompany((prev) =>
                    prev.filter((i) => i.title !== item.title)
                );
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        // if (isInitialRender) {
        //   setIsInitialRender(false); // Mark as rendered
        //   return; // Skip first execution
        // }
        const fetchdataInUseEffect = async () => {
            // fetchCURFQAssignedToMe(getLoggedInUserID());
            setAnchorEl(null);
            setSelectedKeyAccountManager([]);
            setSelectedStatus([]);
            setSelectedCompany([]);
        };
        fetchdataInUseEffect();
    }, []);

    const handleClearAll = () => {
        setSelectedKeyAccountManager([]);
        setSelectedStatus([]);
        setSelectedCompany([]);
    };

    const handleClose = () => {
        setSelectedKeyAccountManager(filteredSelectedKeyAccountManager);
        setSelectedStatus(filteredSelectedStatus);
        setSelectedCompany(filteredSelectedCompany);

        setAnchorEl(null);
    };

    const handleClearAllChip = () => {
        setSelectedKeyAccountManager([]);
        setSelectedStatus([]);
        setSelectedCompany([]);

        setFilteredSelectedKeyAccountManager([]);
        setFilteredSelectedStatus([]);
        setFilteredSelectedCompany([]);

        setFilteredRows(rows);
        setIsApplyFilter(false);
    };

    // Run `handleApply` after any filter changes
    useEffect(() => {
        handleApply();
        if (
            selectedStatus?.length == 0 &&
            selectedKeyAccountManager?.length == 0 &&
            selectedCompany?.length === 0
        ) {
            setIsApplyFilter(false);
        }
    }, [removeFilter]);

    useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [searchTerm]);

    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

    const handleFileChange = (e, uploadWrapper) => {
        e.stopPropagation();

        const file = e.target.files?.[0];
        if (!file) return;

        // 1️⃣ PDF validation
        if (file.type !== "application/pdf") {
            showToast({
                message: "Only PDF is allowed",
                type: "error"
            });
            e.target.value = "";
            return;
        }

        // 2️⃣ Size validation
        if (file.size > MAX_FILE_SIZE) {
            const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
            showToast({
                message: `Uploaded file must be less than 50 MB`,
                type: "error"
            });
            e.target.value = "";
            return;
        }

        // ✅ Validation passed → call existing controller logic
        uploadWrapper(e);
    };

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isUploadingGlobal) {
                e.preventDefault();
                e.returnValue = ""; // required for Chrome
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isUploadingGlobal]);

    const ActionMenu = ({ row }) => {
        const [anchorEl, setAnchorEl] = useState(null);
        const open = Boolean(anchorEl);

        const handleClick = (event: { currentTarget: any }) => {
            setAnchorEl(event.currentTarget);
        };

        const handleClose = () => {
            setAnchorEl(null);
        };

        const payload = {
            rfqData: {
                ID: row?.CORFQ_ID,
                rfqID: row?.CORFQ_No
            },
            from: "Proposals"
        };

        return (
            <>
                <IconButton onClick={handleClick}>
                    <MoreVertIcon />
                </IconButton>
                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    <MenuItem sx={{ fontSize: "0.875rem" }}>
                        <Link
                            to="/layout/AllMmrfq"
                            target="_blank"
                            style={{ textDecoration: "none" }}
                            state={{ payload }}
                            onClick={() => {
                                localStorage.setItem(
                                    "AllMmrfq",
                                    JSON.stringify(payload)
                                );
                            }}
                        >
                            View Quotes
                        </Link>
                    </MenuItem>
                    {isAccountManager() && (
                        <>
                            {" "}
                            <MenuItem
                                sx={{ fontSize: "0.875rem" }}
                                disabled={row?.Proposal_Status !== "Approved"}
                            >
                                <Link
                                    to={"/layout/CoPoCreation"}
                                    style={{
                                        textDecoration: "none",
                                        pointerEvents:
                                            row?.Proposal_Status === "Approved"
                                                ? "auto"
                                                : "none",
                                        color:
                                            row?.Proposal_Status === "Approved"
                                                ? "inherit"
                                                : "gray"
                                    }}
                                    state={{
                                        rfqData: {
                                            ID: row?.CORFQ_ID,
                                            rfqID: row?.CORFQ_No,
                                            companyName: row?.Company_Name,
                                            keyAccountManager: row?.KAM,
                                            companyID: row?.Company_ID
                                        },
                                        from: "Proposals"
                                    }}
                                    onClick={() => dispatch(resetCopoData())}
                                >
                                    {"Create COPO"}
                                </Link>
                            </MenuItem>
                            {/* {row?.CoPo_Created && (
                <MenuItem
                  sx={{ fontSize: "0.875rem" }}
                  disabled={
                    !row?.CoPo_Created && row?.Proposal_Status !== "Approved"
                  }
                >
                  <Link
                    to={row?.CoPo_Created ? "/layout/CoPoDatatable" : ""}
                    style={{
                      textDecoration: "none",
                      pointerEvents:
                        row?.CoPo_Created || row?.Proposal_Status === "Approved"
                          ? "auto"
                          : "none",
                      color:
                        row?.CoPo_Created || row?.Proposal_Status === "Approved"
                          ? "inherit"
                          : "gray"
                    }}
                    state={{
                      rfqData: {
                        ID: row?.CORFQ_ID,
                        rfqID: row?.CORFQ_No,
                        companyName: row?.Company_Name,
                        keyAccountManager: row?.KAM,
                        companyID: row?.Company_ID
                      },
                      from: "Proposals"
                    }}
                    onClick={() => !row?.CoPo_Created}
                  >
                    {row?.CoPo_Created ? "View COPO" : ""}
                  </Link>
                </MenuItem>
              )} */}
                        </>
                    )}
                </Menu>
            </>
        );
    };

    const columns: GridColDef[] = [
        {
            field: "CORFQ_No",
            headerName: "CORFQ ID",
            width: 120,
            disableColumnMenu: true,
            // renderCell: (params) => (params.value ? params.value : "-"),
            renderCell: (params) => {
                const payload = {
                    rfqData: {
                        ID: params.row.CORFQ_ID,
                        rfqID: params.row.CORFQ_No,
                        notificationID: params.row.notificationID,
                        isNew: params.row.isNew
                    },
                    from: "Proposals"
                };
                return (
                    <Link
                        to="/layout/CURFQDetails"
                        target="_blank"
                        // state={{ rfqData: params?.row, from: "Proposals" }}
                        state={{payload}}
                        onClick={() => {
                            localStorage.setItem(
                                "CURFQDetails",
                                JSON.stringify(payload)
                            );
                        }}
                        style={{
                            textDecoration: "none",
                            display: "flex",
                            justifyContent: "space-between"
                        }}
                    >
                        <span>{params?.value}</span>
                    </Link>
                );
            }
        },
        {
            field: "CORFQ_Name",
            headerName: "CORFQ Name",
            width: 250,
            disableColumnMenu: true,
            // renderCell: (params) => (params.value ? params.value : "-"),
            renderCell: (params) => {
                const payload = {
                    rfqData: {
                        ID: params.row.CORFQ_ID,
                        rfqID: params.row.CORFQ_No,
                        notificationID: params.row.notificationID,
                        isNew: params.row.isNew
                    },
                    from: "Proposals"
                };
                return (
                    <Link
                        to="/layout/CURFQDetails"
                        target="_blank"
                        // state={{ rfqData: params?.row, from: "Proposals" }}
                        state={{payload}}
                        onClick={() => {
                            localStorage.setItem(
                                "CURFQDetails",
                                JSON.stringify(payload)
                            );
                        }}
                        style={{
                            textDecoration: "none",
                            display: "flex",
                            justifyContent: "space-between"
                        }}
                    >
                        <span>{params?.value}</span>
                    </Link>
                );
            }
        },
        {
            field: "Company_Name",
            headerName: "Company Name",
            minWidth: 300,
            disableColumnMenu: true,
            renderCell: (params) => {
                const name = params.value || "";
                const truncatedName = name;
                const logoUrl = params.row.Company_Logo
                    ? generateCompanyLogo(
                          params.row.Company_ID,
                          params.row.Company_Logo
                      )
                    : defaultlogo;
                return (
                    // <Link
                    //   to=""
                    //   // state={{
                    //   //   companyRecId: params.row?.companyRecId,
                    //   //   compDetails: params.row,
                    //   //   from: getFromValue(),
                    //   // }}
                    //   style={{ textDecoration: "none" }}
                    // >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            height: "100%"
                            // cursor: "pointer",
                        }}
                    >
                        {logoUrl && (
                            <img
                                src={logoUrl}
                                alt={name}
                                width={28}
                                height={28}
                                style={{
                                    borderRadius: "50%",
                                    border: "1px solid #D8D8D8"
                                }}
                            />
                        )}
                        <Typography variant="body2">{truncatedName}</Typography>
                    </Box>
                    // </Link>
                );
            }
        },
        ...(accountManagerHead() || isManagement()
            ? [
                  // {
                  //   field: "Key_Account_Manager",
                  //   headerName: "Key Account Manager",
                  //   width: 150,
                  //   disableColumnMenu: true,
                  //   renderCell: (params) => (params.value ? params.value : "-"),
                  // },
                  {
                      field: "KAM",
                      headerName: "Key Account Manager",
                      width: 210,
                      disableColumnMenu: true,
                      renderCell: (params) => {
                          const name = params.value || "";
                          return (
                              <Grid
                                  container
                                  spacing={2}
                                  alignItems="center"
                                  width="100%"
                              >
                                  <Grid size={{ xs: 2, md: 2 }}>
                                      <img
                                          src={
                                              generatProfilePicUrl(
                                                  params.row.KAM_ID,
                                                  params.row.KAM_Pic,
                                                  "Profile_Picture"
                                              ) || defaultlogo
                                          }
                                          alt="KAM"
                                          width={28}
                                          height={28}
                                          style={{
                                              marginRight: "5px",
                                              borderRadius: "50%",
                                              border: "1px solid #D8D8D8"
                                          }}
                                      />
                                  </Grid>
                                  <Grid size={{ xs: 10, md: 10 }}>
                                      <Typography variant="body2" className="">
                                          {name}
                                      </Typography>
                                  </Grid>
                              </Grid>
                          );
                      }
                  }
              ]
            : []),

        {
            field: "Proposal_Status",
            headerName: "Proposal Status",
            width: 180,
            disableColumnMenu: true,
            renderCell: (params) => {
                const hasFile = Boolean(params.row?.Proposal_File_Path);

                // Determine the current displayed value
                const currentValue =
                    params.row?.Proposal_Status ??
                    (hasFile ? "Uploaded" : "Yet to Upload");

                // Status colors
                const statusColors: Record<string, string> = {
                    "Yet to Upload": "#9e9e9e", // grey
                    Uploaded: "#1976d2", // blue
                    Approved: "#2e7d32", // green
                    Rejected: "#d32f2f" // red
                };

                // Handle status change
                const handleStatusChange = async (newStatus: string) => {
                    // Optimistically update UI
                    params.api.updateRows([
                        { id: params.id, Proposal_Status: newStatus }
                    ]);

                    try {
                        await updateProposalStatus(newStatus, params);
                    } catch (err) {
                        console.error("Failed to update status:", err);
                        // Revert UI on error
                        params.api.updateRows([
                            { id: params.id, proposalStatus: currentValue }
                        ]);

                        showToast({
                            message:
                                err?.response?.data?.message ||
                                err?.message ||
                                "Failed to update proposal status",
                            type: "error"
                        });
                    }
                };

                // If "Yet to Upload" → grey outlined pill, no dropdown
                if (
                    currentValue === "Yet to Upload" ||
                    currentValue === "Uploading"
                ) {
                    return (
                        <Chip
                            label="Yet to Upload"
                            size="small"
                            variant="outlined"
                            sx={{
                                borderColor: statusColors["Yet to Upload"],
                                color: statusColors["Yet to Upload"],
                                fontWeight: 500
                            }}
                        />
                    );
                }

                // File uploaded → outlined colored pill with dropdown
                return (
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                            IconComponent={
                                isAccountManager() ? undefined : () => null
                            }
                            value={currentValue}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => handleStatusChange(e.target.value)}
                            displayEmpty
                            renderValue={(selected) => (
                                <Chip
                                    label={selected || "Uploaded"}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                        borderColor: statusColors[selected],
                                        color: statusColors[selected],
                                        fontWeight: 500
                                    }}
                                />
                            )}
                            sx={{
                                "& fieldset": { border: "none" },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none"
                                },
                                "& .MuiSelect-select": {
                                    padding: "2px 8px",
                                    display: "flex",
                                    alignItems: "center",
                                    fontSize: "0.8rem"
                                },
                                "& .MuiSelect-icon": {
                                    display:
                                        currentValue === "Approved" &&
                                        params?.row?.CoPo_Created
                                            ? "none"
                                            : "block"
                                },
                                height: 28,
                                pointerEvents:
                                    isAccountManager() &&
                                    !params.row?.CoPo_Created
                                        ? "auto"
                                        : "none"
                            }}
                            MenuProps={{ onClick: (e) => e.stopPropagation() }}
                        >
                            <MenuItem value="Uploaded" disabled>
                                Uploaded
                            </MenuItem>
                            <MenuItem value="Approved">Approved</MenuItem>
                            <MenuItem value="Rejected">Rejected</MenuItem>
                        </Select>
                    </FormControl>
                );
            }
        },
        {
            field: "Upload_Proposal",
            headerName: "Upload Proposal",
            width: 220,
            disableColumnMenu: true,
            cellClassName: () => "first-pinned-right",
            headerClassName: "first-pinned-right",
            renderCell: (params) => {
                const canUpload = accountManager();
                const isUploading = params.row?.Proposal_Uploading;
                const isDeleting = params.row?.Proposal_Deleting;

                const uploadWrapper = async (event) => {
                    event.stopPropagation();
                    const file = event.target.files?.[0];
                    if (!file) return;

                    setIsUploadingGlobal(true); // 🔒 lock refresh

                    const formData = new FormData();
                    formData.append("file", file);
                    formData.append("CORFQ_ID", params.row?.CORFQ_ID || "");

                    try {
                        await handleUploadProposal(formData, params);
                    } catch {
                        alert("Upload failed");
                    } finally {
                        event.target.value = "";
                    }
                };

                const deleteWrapper = async (e) => {
                    e.stopPropagation();
                    console.log("DELETE CLICKED");

                    const ok = await confirm();
                    if (!ok) return;

                    try {
                        await handleDeleteProposal(
                            params.id,
                            params.row?.Proposal_ID,
                            params
                        );
                    } catch (err) {
                        showToast({
                            message:
                                err?.response?.data?.message ||
                                err?.message ||
                                "Failed to delete proposal",
                            type: "error"
                        });
                    }
                };

                const proposalPath = proposalDownloadURL(
                    params.row?.Proposal_File_Path
                );
                const proposalName = params.row?.Proposal_File_Name;

                // Loader
                if (isUploading || isDeleting) {
                    return (
                        <div
                            style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "center"
                            }}
                        >
                            <CircularProgress size={18} />
                            <Typography variant="caption">
                                {isDeleting ? "Deleting…" : "Uploading…"}
                            </Typography>
                        </div>
                    );
                }

                // File exists
                if (proposalPath) {
                    return (
                        <div style={{ display: "flex", gap: 8 }}>
                            <Button
                                size="small"
                                startIcon={<DescriptionIcon fontSize="small" />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(proposalPath, "_blank");
                                }}
                            >
                                {proposalName || "Proposal"}
                            </Button>

                            {isAccountManager() &&
                                !params?.row?.CoPo_Created && (
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onMouseDown={(e) => {
                                            e.stopPropagation();
                                            deleteWrapper(e);
                                        }}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                )}
                        </div>
                    );
                }

                // Upload allowed for the level 1 manager of KAM
                if (canUpload) {
                    return (
                        <Button
                            component="label"
                            size="small"
                            startIcon={<UploadFileIcon />}
                            onClick={(e) => e.stopPropagation()}
                        >
                            Upload
                            <input
                                type="file"
                                hidden
                                accept="application/pdf"
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) =>
                                    handleFileChange(e, uploadWrapper)
                                }
                            />
                        </Button>
                    );
                }

                return (
                    <Typography variant="body2">No File Uploaded</Typography>
                );
            }
        },
        {
            field: "Last_Updated_Time",
            headerName: "Last Updated On",
            width: 180,
            disableColumnMenu: true,
            renderCell: (params) => (params.value ? params.value : "-"),
            sortComparator: (v1, v2) =>
                parseCustomDate(v1).getTime() - parseCustomDate(v2).getTime()
        },
        {
            field: "Action",
            headerName: "Action",
            width: 100,
            disableColumnMenu: true,
            renderCell: (params) => <ActionMenu row={params.row} />
        }
    ];

    return (
        <>
            <Breadcrumbs aria-label="breadcrumb" sx={{ marginBottom: "10px" }}>
                <Typography sx={{ fontSize: "14px" }}>
                    <Link
                        to="/layout/Dashboard"
                        style={{
                            color: "#0070BA",
                            fontSize: "14px",
                            textDecoration: "none"
                        }}
                    >
                        Home
                    </Link>
                </Typography>
                {[
                    <>
                        <Typography key="final" sx={{ fontSize: "14px" }}>
                            Proposals
                        </Typography>
                    </>
                ]}
            </Breadcrumbs>

            <Typography variant="h5" mb={1}>
                Proposals
            </Typography>
            <Box
                sx={{
                    borderRadius: "5px",
                    marginTop: "16px",
                    background: "#fff",
                    padding: "15px"
                }}
            >
                <Grid container spacing={2} mb={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        {/* Search Section */}
                        {allRows?.length > 1 && (
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 7 }}>
                                    <TextField
                                        label="Search by CORFQ ID & CORFQ Name"
                                        size="small"
                                        variant="outlined"
                                        fullWidth
                                        inputRef={searchInputRef}
                                        value={searchTerm}
                                        onChange={handleSearch}
                                    />
                                </Grid>

                                {/* Filter section */}
                                <Grid
                                    size={{ xs: 12, md: 5 }}
                                    sx={{
                                        color: "grey",
                                        display: "flex",
                                        alignItems: "center"
                                    }}
                                >
                                    <Box>
                                        <IconButton onClick={handleFilterClick}>
                                            <Badge
                                                badgeContent={
                                                    filteredSelectedStatus?.length +
                                                    filteredSelectedKeyAccountManager?.length +
                                                    filteredSelectedCompany?.length
                                                }
                                                color="primary"
                                            >
                                                <FilterAltOutlinedIcon />
                                            </Badge>
                                        </IconButton>

                                        <Popper
                                            open={Boolean(anchorEl)}
                                            anchorEl={anchorEl}
                                            placement="bottom-start"
                                            sx={{ zIndex: 13 }}
                                        >
                                            <Paper
                                                ref={popperRef}
                                                sx={{
                                                    p: 2,
                                                    width: 350,
                                                    boxShadow: 3,
                                                    borderRadius: 2
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        fontWeight: "bold",
                                                        mb: 2,
                                                        display: "flex",
                                                        justifyContent:
                                                            "space-between"
                                                    }}
                                                >
                                                    Filter{" "}
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={handleClearAll}
                                                    >
                                                        CLEAR ALL
                                                    </Button>
                                                </Box>
                                                {!isFilterLoading ? (
                                                    <Box sx={{ mb: 2 }}>
                                                        {/* {(sourcingManagerHead() || Management()) && (
                          <Autocomplete
                            multiple
                            limitTags={2}
                            sx={{ marginBottom: "15px" }}
                            size="small"
                            id="multiple-limit-tags"
                            value={selectedSourcingManager}
                            onChange={(event, newValue) =>
                              setSelectedSourcingManager(newValue || [])
                            }
                            options={[
                              ...(filterOptions?.sourcingManagerOptions || []),
                            ]?.sort((a, b) =>
                              a?.title?.localeCompare(b?.title)
                            )}
                            getOptionLabel={(option) => option?.title || ""}
                            renderOption={(props, option) => (
                              <li
                                {...props}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                }}
                              >
                                <Typography>{option.title}</Typography>
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Team Member"
                                placeholder="Select Team Member"
                              />
                            )}
                          />
                        )} */}

                                                        {(accountManagerHead() ||
                                                            Management()) && (
                                                            <Autocomplete
                                                                multiple
                                                                size="small"
                                                                sx={{
                                                                    marginBottom:
                                                                        "15px"
                                                                }}
                                                                limitTags={2}
                                                                id="multiple-limit-tags"
                                                                options={[
                                                                    ...(filterOptions?.keyAccountManagerOptions ||
                                                                        [])
                                                                ]?.sort(
                                                                    (a, b) =>
                                                                        a?.title?.localeCompare(
                                                                            b?.title
                                                                        )
                                                                )}
                                                                getOptionLabel={(
                                                                    option
                                                                ) =>
                                                                    option?.title ||
                                                                    ""
                                                                }
                                                                value={
                                                                    selectedKeyAccountManager
                                                                }
                                                                onChange={(
                                                                    event,
                                                                    newValue
                                                                ) =>
                                                                    setSelectedKeyAccountManager(
                                                                        newValue ||
                                                                            []
                                                                    )
                                                                }
                                                                isOptionEqualToValue={(
                                                                    option,
                                                                    value
                                                                ) =>
                                                                    option.title ===
                                                                    value?.title
                                                                }
                                                                renderInput={(
                                                                    params
                                                                ) => (
                                                                    <TextField
                                                                        {...params}
                                                                        label="Key Account Manager"
                                                                        placeholder="Select Key Account Manager"
                                                                    />
                                                                )}
                                                            />
                                                        )}

                                                        <Autocomplete
                                                            multiple
                                                            size="small"
                                                            sx={{
                                                                marginBottom:
                                                                    "15px"
                                                            }}
                                                            limitTags={2}
                                                            id="multiple-limit-tags"
                                                            options={[
                                                                ...(filterOptions?.companyOptions ||
                                                                    [])
                                                            ]?.sort((a, b) =>
                                                                a?.title?.localeCompare(
                                                                    b?.title
                                                                )
                                                            )}
                                                            value={
                                                                selectedCompany
                                                            }
                                                            onChange={(
                                                                event,
                                                                newValue
                                                            ) =>
                                                                setSelectedCompany(
                                                                    newValue ||
                                                                        []
                                                                )
                                                            }
                                                            getOptionLabel={(
                                                                option
                                                            ) =>
                                                                option?.title ||
                                                                ""
                                                            }
                                                            isOptionEqualToValue={(
                                                                option,
                                                                value
                                                            ) =>
                                                                option.title ===
                                                                value?.title
                                                            }
                                                            //  disabled={(selectedIndustry || []).length == 0}
                                                            renderInput={(
                                                                params
                                                            ) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Company"
                                                                    placeholder="Select Company"
                                                                />
                                                            )}
                                                        />

                                                        <Autocomplete
                                                            multiple
                                                            size="small"
                                                            limitTags={2}
                                                            sx={{
                                                                marginBottom:
                                                                    "15px"
                                                            }}
                                                            id="multiple-limit-tags"
                                                            options={(
                                                                filterOptions?.statusOptions ||
                                                                []
                                                            ).sort((a, b) =>
                                                                a?.title?.localeCompare(
                                                                    b?.title
                                                                )
                                                            )}
                                                            getOptionLabel={(
                                                                option
                                                            ) =>
                                                                option?.title ||
                                                                ""
                                                            }
                                                            value={
                                                                selectedStatus
                                                            }
                                                            onChange={(
                                                                event,
                                                                newValue
                                                            ) =>
                                                                setSelectedStatus(
                                                                    newValue ||
                                                                        []
                                                                )
                                                            }
                                                            isOptionEqualToValue={(
                                                                option,
                                                                value
                                                            ) =>
                                                                option.title ===
                                                                value?.title
                                                            }
                                                            renderInput={(
                                                                params
                                                            ) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Proposal Status"
                                                                    placeholder="Select Proposal Status"
                                                                />
                                                            )}
                                                        />
                                                    </Box>
                                                ) : (
                                                    <Box
                                                        display="flex"
                                                        justifyContent="center"
                                                        alignItems="center"
                                                        height="50vh"
                                                    >
                                                        <ThreeDot
                                                            color="#1976d2"
                                                            size="medium"
                                                            text=""
                                                            textColor=""
                                                        />
                                                    </Box>
                                                )}
                                                {/* Apply / Cancel Buttons */}
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "space-between",
                                                        mt: 2
                                                    }}
                                                >
                                                    <Button
                                                        onClick={handleClose}
                                                        color="error"
                                                    >
                                                        CANCEL
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleApply}
                                                    >
                                                        APPLY
                                                    </Button>
                                                </Box>
                                            </Paper>
                                        </Popper>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    {/* Selected Filters as Chips */}
                    {isApplyFilter == true && (
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 1,
                                "& .MuiPopper-root": {
                                    zIndex: 100
                                }
                            }}
                            mb={2}
                        >
                            {filteredSelectedStatus?.map((item, index) => (
                                <Chip
                                    key={`status-${index}`}
                                    label={item.title}
                                    onDelete={() =>
                                        handleRemoveFilter("status", item)
                                    }
                                    size="small"
                                />
                            ))}
                            {filteredSelectedKeyAccountManager?.map(
                                (item, index) => (
                                    <Chip
                                        key={`keyAccountManager-${index}`}
                                        label={item.title}
                                        onDelete={() =>
                                            handleRemoveFilter(
                                                "keyAccountManager",
                                                item
                                            )
                                        }
                                        size="small"
                                    />
                                )
                            )}
                            {filteredSelectedCompany?.map((item, index) => (
                                <Chip
                                    key={`company-${index}`}
                                    label={item?.title}
                                    onDelete={() =>
                                        handleRemoveFilter("company", item)
                                    }
                                    size="small"
                                />
                            ))}
                            {/* Clear All Button - Show only when filters exist */}
                            {filteredSelectedStatus?.length > 0 && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handleClearAllChip}
                                    sx={{
                                        fontSize: ".75rem !important",
                                        lineHeight: "1",
                                        borderRadius: "30px"
                                    }}
                                >
                                    CLEAR ALL
                                </Button>
                            )}
                        </Box>
                    )}
                </Grid>
                <Paper
                    sx={{
                        // height: 400,
                        height:
                            filteredRows?.length > 4
                                ? "62vh"
                                : filteredRows?.length > 0
                                  ? "auto"
                                  : "62vh",
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center"
                    }}
                >
                    {!isLoading ? (
                        (isApplyFilter ? filteredRows : rows)?.length > 0 ? (
                            <>
                                <DataGridPro
                                    rows={isApplyFilter ? filteredRows : rows}
                                    columns={columns}
                                    getRowId={(row) => row?.id}
                                    pagination={
                                        (isApplyFilter ? filteredRows : rows)
                                            ?.length > 10
                                    }
                                    rowSelectionModel={selectedRows}
                                    pinnedColumns={pinnedColumns}
                                    onPinnedColumnsChange={
                                        handlePinnedColumnsChange
                                    }
                                    paginationModel={paginationModel}
                                    onPaginationModelChange={setPaginationModel}
                                    pageSizeOptions={[10, 20]}
                                    disableRowSelectionOnClick={true}
                                    initialState={{
                                        sorting: {
                                            sortModel: [
                                                {
                                                    field: "Last_Updated_Time",
                                                    sort: "desc"
                                                }
                                            ]
                                        }
                                    }}
                                />
                                <ConfirmDialog />
                            </>
                        ) : (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: "8px",
                                    padding: "20px"
                                }}
                            >
                                {/* If user searched and original rows are empty */}
                                {searchTerm && (!rows || rows.length === 0) ? (
                                    "No Options available"
                                ) : isApplyFilter &&
                                  (!filteredRows ||
                                      filteredRows.length === 0) ? (
                                    // When filters are applied but no results
                                    "No results for applied filters"
                                ) : (
                                    // Default empty state
                                    <>
                                        <img
                                            src={emptyImage}
                                            alt="Empty"
                                            width="300px"
                                        />
                                        <b style={{ color: "#0009" }}>
                                            All Proposals Will Appear here
                                        </b>
                                    </>
                                )}
                            </div>
                        )
                    ) : (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="50vh"
                        >
                            <ThreeDot
                                color="#1976d2"
                                size="medium"
                                text=""
                                textColor=""
                            />
                        </Box>
                    )}
                </Paper>
            </Box>
        </>
    );
};
